import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import JourneyPosition from "@/components/JourneyPosition";
import api from "@/lib/api";
import Icon from "@/components/Icon";
import { toast } from "sonner";
import { GraduationCap, BookOpen, Target, Sparkles, Star, ArrowRight, Briefcase, Check, Layers, Route } from "lucide-react";

// Peta kata kunci prodi/jurusan -> bidang katalog (untuk memetakan rekomendasi iTi teks-bebas ke bidang)
const BIDANG_KEYWORDS = {
  teknik: ["teknik", "rekayasa", "sipil", "mesin", "elektro", "arsitek", "insinyur", "industri"],
  kesehatan: ["kedokteran", "dokter", "farmasi", "keperawatan", "perawat", "gizi", "gigi", "medical", "kesehatan", "nursing", "therapist", "apoteker", "bidan"],
  sains: ["matematika", "fisika", "kimia", "biologi", "statistika", "aktuaria", "mipa", "geologi", "astronomi"],
  ti: ["komputer", "sistem informasi", "sains data", "informatika", "teknologi informasi", "programmer", "software", "siber", "data science"],
  bisnis: ["bisnis", "ekonomi", "manajemen", "akuntansi", "marketing", "sales", "entrepreneur", "wirausaha", "pariwisata", "hospitality", "travel", "perhotelan", "keuangan"],
  hukum: ["hukum", "hubungan internasional", "politik", "administrasi publik", "notaris", "pengacara"],
  psikologi: ["psikologi", "psikolog", "konselor", "konseling", "pendidikan", "guru", "keguruan", "fkip", "paud", "sdm", "motivator", "bimbingan", "special education"],
  seni: ["seni", "desain", "dkv", "komunikasi visual", "sastra", "film", "televisi", "musik", "drama", "kesenian", "interior", "produk", "fashion", "animasi"],
  komunikasi: ["jurnalistik", "public relations", "broadcasting", "periklanan", "humas", "penyiaran", "reporter"],
};

const matchBidangIds = (texts) => {
  const ids = [];
  (texts || []).forEach((t) => {
    const s = (t || "").toLowerCase();
    let best = null, bestScore = 0;
    for (const [id, kws] of Object.entries(BIDANG_KEYWORDS)) {
      const score = kws.reduce((a, k) => a + (s.includes(k) ? 1 : 0), 0);
      if (score > bestScore) { bestScore = score; best = id; }
    }
    if (best && !ids.includes(best)) ids.push(best);
  });
  return ids;
};

export default function JelajahiBidang() {
  const { user } = useAuth();
  const [content, setContent] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [journey, setJourney] = useState({ mapel_pilihan: [], bidang_minat: null });
  const [saving, setSaving] = useState(false);
  const [partner, setPartner] = useState(null);

  useEffect(() => {
    api.get("/journey/content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/journey/me").then((r) => setJourney({ mapel_pilihan: r.data.journey?.mapel_pilihan || [], bidang_minat: r.data.journey?.bidang_minat || null })).catch(() => {});
    api.get("/assessment/result").then((r) => setAssessment(r.data.result)).catch(() => {});
    api.get("/assessment/partner").then((r) => setPartner(r.data.report)).catch(() => {});
  }, []);

  const currentKelas = user?.kelas;
  const isKelas12 = currentKelas === 12;

  const saveBidang = async (bidang_minat) => {
    setSaving(true);
    try {
      await api.post("/journey/save", { mapel_pilihan: journey.mapel_pilihan, bidang_minat, kelas: currentKelas });
      setJourney((j) => ({ ...j, bidang_minat }));
      toast.success("Bidang minat tersimpan");
    } catch { toast.error("Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  if (!content) return <AppShell><p className="text-[#5C5F5C]">Memuat…</p></AppShell>;

  const n = assessment?.narrative;
  const topRek = assessment?.scores?.rekomendasi || [];
  const jalurRek = n?.jalur_rekomendasi;
  const prodiRek = n?.prodi_rekomendasi?.length ? n.prodi_rekomendasi : (partner?.data?.rekomendasi_jurusan_ptn || []);
  const kedinasanRek = partner?.data?.rekomendasi_kedinasan || [];
  const utbk = n?.utbk_subtes || [];

  // Info mendalam: bidang diturunkan dari rekomendasi asesmen internal (dgn match%) DAN/ATAU rekomendasi iTi.
  const itiProdi = partner?.data?.rekomendasi_jurusan_ptn || [];
  const itiBidangIds = matchBidangIds(itiProdi);
  const hasRec = topRek.length > 0 || itiBidangIds.length > 0;
  const asmtBidang = topRek
    .map((r) => { const full = content.bidang.find((b) => b.id === r.id); return full ? { ...full, match: r.match } : null; })
    .filter(Boolean);
  const itiBidang = itiBidangIds
    .filter((id) => !asmtBidang.some((b) => b.id === id))
    .map((id) => { const full = content.bidang.find((b) => b.id === id); return full ? { ...full, match: null } : null; })
    .filter(Boolean);
  const detailBidang = hasRec
    ? [...asmtBidang, ...itiBidang]
    : content.bidang.slice(0, 3).map((b) => ({ ...b, match: null }));

  return (
    <AppShell>
      <div className="mb-8 max-w-2xl">
        <h1 className="font-heading font-black text-3xl sm:text-4xl text-[#1A1C1A]">Jelajahi Bidang</h1>
        <p className="text-[#5C5F5C] mt-2">{isKelas12 ? "Mantapkan arah pendidikanmu — jalur PTN/Kedinasan, prodi yang sesuai, dan prospek kariernya." : "Pahami mendalam bidang ilmu yang paling sesuai denganmu — mata pelajaran, prodi, dan kariernya."}</p>
      </div>

      {user?.role !== "orangtua" && (
        <JourneyPosition current="jelajahi" note={
          <p className="text-sm text-[#1A1C1A] leading-relaxed">
            <span className="font-semibold">Kamu ada di sini sekarang.</span> Pahami bidang yang paling sesuai denganmu, lalu <span className="font-semibold">tandai minat utamamu</span> agar rekomendasi mapel & langkah berikutnya makin tepat. Kamu bisa menggantinya kapan saja. 🌱
          </p>
        } />
      )}

      {/* Personalisasi dari hasil asesmen siswa yang login */}
      {assessment ? (
        <div className="clay-card p-7 bg-[#1A1C1A] text-white border-[#1A1C1A] mb-8" data-testid="jelajahi-personal">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-[#F4B942] text-sm font-medium mb-3"><Sparkles className="h-4 w-4" /> Berdasarkan hasil asesmenmu</div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              {assessment.cita_cita && (
                <p className="text-white/90 mb-3 flex items-center gap-2"><Star className="h-4 w-4 text-[#F4B942]" /> Cita-cita: <b>{assessment.cita_cita}</b></p>
              )}
              <p className="text-white/70 text-sm mb-2">Bidang yang paling selaras denganmu:</p>
              <div className="flex flex-wrap gap-2">
                {topRek.map((b) => (
                  <span key={b.id} className="px-3 py-1.5 rounded-full text-sm font-medium" style={{ background: b.warna }}>{b.nama} · {b.match}%</span>
                ))}
              </div>
            </div>
            {isKelas12 ? (
              (prodiRek.length > 0 || utbk.length > 0) && (
                <div>
                  <p className="text-white/70 text-sm mb-2">Prodi & jalur yang sesuai untukmu:</p>
                  {prodiRek.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {prodiRek.slice(0, 6).map((p, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-white/10 text-white">{p}</span>)}
                    </div>
                  )}
                  {utbk.length > 0 && <p className="text-white/70 text-xs">Subtes UTBK fokus: <span className="text-white">{utbk.join(", ")}</span></p>}
                </div>
              )
            ) : n?.mapel_prioritas?.length > 0 && (
              <div>
                <p className="text-white/70 text-sm mb-2">Mapel prioritas untuk diperkuat:</p>
                <ul className="space-y-1.5">
                  {n.mapel_prioritas.map((m, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/90"><BookOpen className="h-4 w-4 text-[#F4B942] mt-0.5 shrink-0" /> {m.mapel}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <Link to="/profilku" className="mt-4 inline-flex items-center gap-2 text-sm text-[#F4B942] font-medium">Lihat hasil lengkap <ArrowRight className="h-4 w-4" /></Link>
        </div>
      ) : (
        <div className="clay-card p-6 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="jelajahi-no-assessment">
          <p className="text-sm text-[#5C5F5C] max-w-md">Selesaikan Asesmen Potensi Diri agar bidang & mapel di halaman ini menyesuaikan hasilmu, bukan konten umum.</p>
          <Link to="/asesmen" className="px-5 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] transition-colors shrink-0">Mulai Asesmen</Link>
        </div>
      )}

      {/* Kelas 12: Jalur PTN & Kedinasan + prodi (selaras iTi) — pengganti fokus mapel */}
      {isKelas12 && (
        <div className="clay-card p-7 mb-12" data-testid="jelajahi-jalur-12">
          <div className="flex items-center gap-2 mb-1"><Route className="h-5 w-5 text-[#E25E3E]" /><h2 className="font-heading font-black text-2xl text-[#1A1C1A]">Jalur Pendidikan yang Sesuai (PTN / Kedinasan)</h2></div>
          <p className="text-[#5C5F5C] mb-5">Di kelas 12, fokusmu bukan lagi memilih mapel, melainkan menentukan jalur ke PTN atau Kedinasan beserta prodinya — selaras dengan hasil asesmen & laporan iTi-mu.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-[#E5E0D8] p-5">
              <p className="text-sm font-semibold text-[#1A1C1A] mb-2 flex items-center gap-1.5"><GraduationCap className="h-4 w-4 text-[#E25E3E]" /> Jalur PTN (SNBP / SNBT / Mandiri)</p>
              {jalurRek?.nama && <p className="text-xs text-[#5C5F5C] mb-2">Disarankan: <span className="font-medium text-[#1A1C1A]">{jalurRek.nama}</span></p>}
              {prodiRek.length > 0 ? (
                <div className="flex flex-wrap gap-1.5" data-testid="jelajahi-prodi-list">{prodiRek.map((p, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-[#E25E3E]/10 text-[#E25E3E] font-medium">{p}</span>)}</div>
              ) : <p className="text-xs text-[#9C9A94]">Selesaikan asesmen atau upload laporan iTi untuk melihat prodi yang sesuai.</p>}
              {utbk.length > 0 && <p className="text-xs text-[#5C5F5C] mt-3">Subtes UTBK yang perlu dikuatkan: <span className="text-[#1A1C1A]">{utbk.join(", ")}</span></p>}
            </div>
            <div className="rounded-xl border border-[#E5E0D8] p-5">
              <p className="text-sm font-semibold text-[#1A1C1A] mb-2 flex items-center gap-1.5"><GraduationCap className="h-4 w-4 text-[#4A6B53]" /> Jalur Kedinasan</p>
              {kedinasanRek.length > 0 ? (
                <div className="flex flex-wrap gap-1.5" data-testid="jelajahi-kedinasan-list">{kedinasanRek.map((p, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-[#4A6B53]/10 text-[#4A6B53] font-medium">{p}</span>)}</div>
              ) : <p className="text-xs text-[#9C9A94]">Belum ada rekomendasi kedinasan dari asesmen iTi.</p>}
            </div>
          </div>
          <Link to="/susun-langkah" className="mt-5 inline-flex items-center gap-2 text-sm text-[#E25E3E] font-medium">Lanjut susun langkah menuju target <ArrowRight className="h-4 w-4" /></Link>
        </div>
      )}

      {/* ===== Info mendalam bidang yang sesuai (tepat di bawah banner) ===== */}
      <div className="mb-12" data-testid="deep-bidang">
        <div className="flex items-center gap-2 mb-2"><Layers className="h-5 w-5 text-[#E25E3E]" /><h2 className="font-heading font-black text-2xl text-[#1A1C1A]">{hasRec ? "Info Mendalam Bidang yang Sesuai Untukmu" : "Info Mendalam Bidang Ilmu"}</h2></div>
        <p className="text-[#5C5F5C] mb-6">{hasRec ? "Ditampilkan sesuai rekomendasi dari asesmen & laporan iTi-mu — pahami bidang-bidang ini sebelum menyusun langkah." : (isKelas12 ? "Pahami setiap bidang ilmu, prodi, dan prospek kariernya untuk memantapkan pilihan jalur PTN/Kedinasanmu." : "Pahami setiap bidang ilmu, mata pelajaran pendukung, prodi, dan kariernya.")}</p>
        <div className="space-y-6">
          {detailBidang.map((b) => {
            const on = journey.bidang_minat === b.id;
            return (
              <div key={b.id} className={`clay-card p-7 ${on ? "ring-2 ring-[#E25E3E]" : ""}`} data-testid={`deep-bidang-${b.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div className="flex items-center gap-4">
                    <span className="h-14 w-14 rounded-2xl grid place-items-center text-white shrink-0" style={{ background: b.warna }}><Icon name={b.ikon} className="h-7 w-7" /></span>
                    <div>
                      <h3 className="font-heading font-black text-xl text-[#1A1C1A]">{b.nama}</h3>
                      <p className="text-sm text-[#5C5F5C] max-w-xl mt-0.5">{b.deskripsi}</p>
                    </div>
                  </div>
                  {b.match != null && <span className="font-heading font-black text-2xl shrink-0" style={{ color: b.warna }}>{b.match}%</span>}
                </div>

                <div className={`grid gap-5 ${isKelas12 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
                  {!isKelas12 && (
                    <div>
                      <p className="text-xs font-semibold text-[#5C5F5C] mb-2 flex items-center gap-1.5"><BookOpen className="h-4 w-4" /> Mata Pelajaran Pendukung</p>
                      <div className="flex flex-wrap gap-1.5">
                        {b.mapel_pendukung.map((m) => <span key={m} className="text-xs px-2.5 py-1 rounded-full bg-[#4A6B53]/10 text-[#4A6B53] font-medium">{m}</span>)}
                      </div>
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-semibold text-[#5C5F5C] mb-2 flex items-center gap-1.5"><GraduationCap className="h-4 w-4" /> Prodi / Jurusan</p>
                    <div className="flex flex-wrap gap-1.5">
                      {b.jurusan.map((j) => <span key={j} className="text-xs px-2.5 py-1 rounded-full bg-[#F0ECE4] text-[#1A1C1A]">{j}</span>)}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#5C5F5C] mb-2 flex items-center gap-1.5"><Briefcase className="h-4 w-4" /> Karier & Prospek</p>
                    <p className="text-sm text-[#1A1C1A]">{b.prospek}</p>
                  </div>
                </div>

                <div className="mt-5 pt-4 border-t border-[#E5E0D8] flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-xs text-[#5C5F5C]">Kekuatan kognitif yang menonjol: {b.kognitif.join(", ")}</span>
                  <button onClick={() => saveBidang(b.id)} disabled={saving} data-testid={`deep-pick-${b.id}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${on ? "bg-[#E25E3E] text-white" : "border-2 border-[#1A1C1A] text-[#1A1C1A] hover:bg-[#1A1C1A]/5"}`}>
                    {on ? <><Target className="h-4 w-4" /> Minat Utama</> : <><Check className="h-4 w-4" /> Jadikan Minat Utama</>}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Semua bidang (pilih minat utama) ===== */}
      <div className="mb-12">
        <div className="flex items-center gap-2 mb-2"><GraduationCap className="h-5 w-5 text-[#E25E3E]" /><h2 className="font-heading font-black text-2xl text-[#1A1C1A]">Semua Bidang Ilmu</h2></div>
        <p className="text-[#5C5F5C] mb-6">Jelajahi bidang lain dan tandai satu sebagai minat utamamu.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {content.bidang.map((b) => {
            const on = journey.bidang_minat === b.id;
            return (
              <button key={b.id} onClick={() => saveBidang(b.id)} data-testid={`bidang-${b.id}`}
                className={`clay-card clay-card-hover p-6 text-left ${on ? "ring-2 ring-[#E25E3E]" : ""}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="h-11 w-11 rounded-xl grid place-items-center text-white" style={{ background: b.warna }}><Icon name={b.ikon} className="h-5 w-5" /></span>
                  {on && <span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#E25E3E] text-white flex items-center gap-1"><Target className="h-3 w-3" /> Minat Utama</span>}
                </div>
                <h3 className="font-heading font-bold text-lg text-[#1A1C1A]">{b.nama}</h3>
                <p className="text-sm text-[#5C5F5C] mt-1">{b.deskripsi}</p>
                <p className="text-xs text-[#5C5F5C] mt-3"><span className="font-semibold text-[#1A1C1A]">Prodi:</span> {b.jurusan.slice(0, 3).join(", ")}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* ===== Peta perjalanan per kelas (menu kelas lain) ===== */}
      <div className="relative">
        <div className="flex items-center gap-2 mb-6"><Sparkles className="h-5 w-5 text-[#E25E3E]" /><h2 className="font-heading font-black text-2xl text-[#1A1C1A]">Peta Perjalanan Kelas 9–12</h2></div>
        <div className="absolute left-5 top-16 bottom-2 w-0.5 bg-[#E5E0D8] hidden sm:block" />
        <div className="space-y-6">
          {content.journey.map((st) => {
            const active = st.kelas === currentKelas;
            return (
              <div key={st.kelas} className="sm:pl-14 relative">
                <span className={`hidden sm:grid place-items-center absolute left-0 top-1 h-11 w-11 rounded-full font-heading font-black text-white ${active ? "bg-[#E25E3E] ring-4 ring-[#E25E3E]/20" : "bg-[#1A1C1A]"}`}>{st.kelas}</span>
                <div className={`clay-card p-6 ${active ? "border-[#E25E3E]/40 ring-1 ring-[#E25E3E]/20" : ""}`} data-testid={`stage-${st.kelas}`}>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <span className="sm:hidden h-8 w-8 rounded-full bg-[#1A1C1A] text-white grid place-items-center text-sm font-bold">{st.kelas}</span>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#4A6B53]/10 text-[#4A6B53]">{`Kelas ${st.kelas} · ${st.jenjang}`}</span>
                    {active && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#E25E3E] text-white">Kelas kamu sekarang</span>}
                  </div>
                  <h3 className="font-heading font-black text-xl text-[#1A1C1A]">{st.judul}</h3>
                  <p className="text-sm text-[#5C5F5C] mt-1 mb-4">{st.deskripsi}</p>
                  <ul className="grid sm:grid-cols-2 gap-2">
                    {st.aktivitas.map((a, k) => (
                      <li key={k} className="flex items-start gap-2 text-sm text-[#1A1C1A]"><Check className="h-4 w-4 text-[#4A6B53] mt-0.5 shrink-0" /> {a}</li>
                    ))}
                  </ul>
                  {st.tipe === "mapel" && (
                    <p className="mt-4 text-sm text-[#5C5F5C] flex items-center gap-2"><BookOpen className="h-4 w-4 text-[#E25E3E]" /> Pilih & simpan mapel prioritasmu di menu <Link to="/susun-langkah" className="text-[#E25E3E] font-medium">Susun Langkah</Link>.</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
